import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

const STATUS_COLORS = {
  PENDING_ACCEPTANCE: "#3b82f6", AWAITING_PAYMENT: "#3b82f6", FUNDED: "#7c3aed",
  IN_PROGRESS: "#7c3aed", DELIVERED: "#16a34a", DISPUTED: "#d97706",
  COMPLETED: "#16a34a", CANCELED: "#6b7280", REFUNDED: "#dc2626",
};

const STATUS_LABELS = {
  PENDING_ACCEPTANCE: "Pending Acceptance",
  AWAITING_PAYMENT: "Awaiting Payment",
  FUNDED: "Paid — Awaiting Fulfillment",
  IN_PROGRESS: "Awaiting Delivery",
  DELIVERED: "Delivered",
  DISPUTED: "Disputed",
  COMPLETED: "Completed",
  CANCELED: "Canceled",
  REFUNDED: "Refunded",
};

const TIMELINE_STEPS = [
  { key: "placed", label: "Order Placed", icon: "🛒" },
  { key: "paid", label: "Payment Confirmed", icon: "💳" },
  { key: "shipped", label: "Shipped", icon: "📦" },
  { key: "delivered", label: "Delivered", icon: "✅" },
  { key: "completed", label: "Completed", icon: "🎉" },
];

function getTimelineStep(order) {
  if (["CANCELED", "REFUNDED"].includes(order.status)) return -1;
  if (order.status === "COMPLETED") return 4;
  if (order.buyerConfirmed) return 4;
  if (order.sellerConfirmed || order.status === "IN_PROGRESS") return 2;
  if (order.payment || order.status === "FUNDED") return 1;
  return 0;
}

export default function OrderDetailPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const loadOrder = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/orders/${orderId}`, { headers: authHeaders() });
      const d = await res.json();
      if (res.ok) setOrder(d);
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  const isBuyer = order?.buyerId === user?.id;
  const isSeller = order?.sellerId === user?.id;

  async function handleBuyerConfirm(received) {
    if (!received) {
      navigate(`/dispute/${order.id}`);
      return;
    }
    setConfirming(true);
    try {
      const res = await fetch(`${API_BASE_URL}/orders/${orderId}/buyer-confirm`, {
        method: "PUT", headers: authHeaders()
      });
      if (res.ok) {
        await loadOrder();
        setShowConfirm(false);
      } else {
        const d = await res.json();
        window.alert(d.error || "Could not confirm receipt");
      }
    } catch { window.alert("Something went wrong"); }
    finally { setConfirming(false); }
  }

  function getOrderTitle(order) {
    if (order.items?.length > 0) {
      const names = order.items.map(i => i.listingItem?.title).filter(Boolean);
      if (names.length === 1) return names[0];
      if (names.length === 2) return `${names[0]} + ${names[1]}`;
      if (names.length > 2) return `${names[0]} + ${names.length - 1} more`;
    }
    return order.listing?.title || "Order";
  }

  function getOrderPhoto(order) {
    for (const item of order.items || []) {
      const photos = item.listingItem?.photoUrls;
      if (photos?.length > 0) return photos[0];
    }
    return null;
  }

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 680, margin: "0 auto", padding: "24px 20px" },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, marginBottom: 20, padding: 0 },
    card: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: 20, marginBottom: 14, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 14 },
    metaRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(59,130,246,0.08)", fontSize: 14 },
    metaLabel: { color: "#64748b" },
    metaValue: { fontWeight: 600, color: "#1e293b" },
    badge: (status) => ({ display: "inline-block", padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, background: `${STATUS_COLORS[status] || "#6b7280"}15`, color: STATUS_COLORS[status] || "#6b7280", border: `1px solid ${STATUS_COLORS[status] || "#6b7280"}30` }),

    // Timeline
    timeline: { display: "flex", flexDirection: "column", gap: 0 },
    timelineStep: (done, current) => ({ display: "flex", gap: 14, alignItems: "flex-start", opacity: done || current ? 1 : 0.4 }),
    timelineIcon: (done, current) => ({ width: 36, height: 36, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0, background: done ? "#3b82f6" : current ? "rgba(59,130,246,0.15)" : "rgba(100,116,139,0.1)", border: done ? "2px solid #3b82f6" : current ? "2px solid rgba(59,130,246,0.4)" : "2px solid rgba(100,116,139,0.2)" }),
    timelineLine: (done) => ({ width: 2, height: 20, background: done ? "#3b82f6" : "rgba(100,116,139,0.15)", margin: "0 17px" }),
    timelineLabel: (done, current) => ({ fontSize: 14, fontWeight: current ? 700 : 600, color: done || current ? "#1e293b" : "#94a3b8", paddingTop: 8 }),

    // Action buttons
    primaryBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15, width: "100%", marginBottom: 10, boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
    successBtn: { background: "#16a34a", color: "#fff", border: "none", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15, width: "100%", marginBottom: 10 },
    dangerOutlineBtn: { background: "rgba(220,38,38,0.06)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.2)", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%" },
    warningBtn: { background: "rgba(217,119,6,0.08)", color: "#d97706", border: "1px solid rgba(217,119,6,0.2)", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%", marginBottom: 10 },
    purpleBtn: { background: "rgba(124,58,237,0.08)", color: "#7c3aed", border: "1px solid rgba(124,58,237,0.2)", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%", marginBottom: 10 },

    // Banners
    successBanner: { display: "flex", alignItems: "center", gap: 12, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 12, padding: 16, marginBottom: 14 },
    infoBanner: { display: "flex", alignItems: "center", gap: 12, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 12, padding: 16, marginBottom: 14 },
    disputeBanner: { display: "flex", alignItems: "center", gap: 12, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 12, padding: 16, marginBottom: 14, cursor: "pointer" },

    // Confirm modal
    overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" },
    modal: { background: "#fff", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 20, padding: 28, width: "100%", maxWidth: 400, boxShadow: "0 20px 60px rgba(59,130,246,0.15)" },
  };

  if (loading) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  if (!order) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Order not found</div>
    </div>
  );

  const orderTitle = getOrderTitle(order);
  const orderPhoto = getOrderPhoto(order);
  const timelineStep = getTimelineStep(order);
  const isCanceled = ["CANCELED", "REFUNDED"].includes(order.status);
  const hasActiveDispute = order.dispute && !["RESOLVED", "CLOSED"].includes(order.dispute?.status);
  const canPay = isBuyer && ["PENDING_ACCEPTANCE", "AWAITING_PAYMENT"].includes(order.status) && !order.payment;
  const canCancel = isBuyer && ["PENDING_ACCEPTANCE", "AWAITING_PAYMENT"].includes(order.status) && !order.payment;
  const sellerCanConfirm = isSeller && ["FUNDED", "IN_PROGRESS"].includes(order.status) && !order.sellerConfirmed;
  const buyerCanConfirm = isBuyer && order.sellerConfirmed && !order.buyerConfirmed && !["COMPLETED", "CANCELED", "REFUNDED"].includes(order.status);
  const canDispute = isBuyer && order.payment && !order.dispute && !order.buyerConfirmed && ["FUNDED", "IN_PROGRESS", "DELIVERED"].includes(order.status);

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.main}>
        <button style={s.backBtn} onClick={() => navigate("/orders")}>← Back to Orders</button>

        {/* Header */}
        <div style={s.card}>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 16 }}>
            {orderPhoto ? (
              <img src={orderPhoto} alt={orderTitle} style={{ width: 72, height: 72, borderRadius: 12, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(59,130,246,0.15)" }} onError={e => e.target.style.display = "none"} />
            ) : (
              <div style={{ width: 72, height: 72, borderRadius: 12, background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(124,58,237,0.1))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, flexShrink: 0 }}>📦</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
                {isBuyer && isSeller ? "YOUR LISTING" : isBuyer ? "BUYING" : "SELLING"}
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 4, lineHeight: 1.2 }}>{orderTitle}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>#{order.id.slice(0, 8)}</div>
              <span style={s.badge(order.status)}>{STATUS_LABELS[order.status] || order.status.replace(/_/g, " ")}</span>
            </div>
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: "#1e40af" }}>${Number(order.totalAmount).toFixed(2)}</div>
        </div>

        {/* Timeline */}
        {!isCanceled && (
          <div style={s.card}>
            <div style={s.sectionTitle}>Order Progress</div>
            <div style={s.timeline}>
              {TIMELINE_STEPS.map((step, i) => {
                const done = timelineStep > i;
                const current = timelineStep === i;
                const isLast = i === TIMELINE_STEPS.length - 1;
                return (
                  <div key={step.key}>
                    <div style={s.timelineStep(done, current)}>
                      <div style={s.timelineIcon(done, current)}>
                        {done ? "✓" : step.icon}
                      </div>
                      <div style={s.timelineLabel(done, current)}>{step.label}</div>
                    </div>
                    {!isLast && <div style={s.timelineLine(done)} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Order Details */}
        <div style={s.card}>
          <div style={s.sectionTitle}>Details</div>
          <div style={{ ...s.metaRow }}>
            <span style={s.metaLabel}>{isBuyer ? "Seller" : "Buyer"}</span>
            <span style={s.metaValue}>{isBuyer ? order.seller?.fullName : order.buyer?.fullName}</span>
          </div>
          <div style={s.metaRow}>
            <span style={s.metaLabel}>Payment</span>
            <span style={{ ...s.metaValue, color: order.payment ? "#16a34a" : "#d97706" }}>{order.payment ? "Yes" : "No"}</span>
          </div>
          <div style={s.metaRow}>
            <span style={s.metaLabel}>Shipped</span>
            <span style={{ ...s.metaValue, color: order.sellerConfirmed ? "#16a34a" : "#94a3b8" }}>{order.sellerConfirmed ? "Yes" : "No"}</span>
          </div>
          <div style={{ ...s.metaRow, borderBottom: "none" }}>
            <span style={s.metaLabel}>Received</span>
            <span style={{ ...s.metaValue, color: order.buyerConfirmed ? "#16a34a" : "#94a3b8" }}>{order.buyerConfirmed ? "Yes" : "No"}</span>
          </div>
          {order.isInPerson && (
            <div style={{ marginTop: 12, padding: "8px 12px", background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 8, fontSize: 12, color: "#3b82f6", fontWeight: 600 }}>
              🤝 In-Person Transaction
            </div>
          )}
        </div>

        {/* Items */}
        {order.items?.length > 0 && (
          <div style={s.card}>
            <div style={s.sectionTitle}>Items</div>
            {order.items.map(item => (
              <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid rgba(59,130,246,0.08)", fontSize: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {item.listingItem?.photoUrls?.[0] && (
                    <img src={item.listingItem.photoUrls[0]} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />
                  )}
                  <div>
                    <div style={{ fontWeight: 600, color: "#1e293b" }}>{item.listingItem?.title || "Item"}</div>
                    <div style={{ fontSize: 12, color: "#64748b" }}>Qty: {item.quantity}</div>
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: "#1e40af" }}>${Number(item.lockedUnitPrice).toFixed(2)}</div>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 12, fontSize: 13, color: "#64748b" }}>
              <span>Platform fee (2.5%)</span><span>${Number(order.platformFee).toFixed(2)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
              <span>Total</span><span style={{ color: "#1e40af" }}>${Number(order.totalAmount).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Dispute info */}
        {order.dispute && (
          <div style={s.disputeBanner} onClick={() => navigate(`/dispute/track/${order.id}`)}>
            <span style={{ fontSize: 24 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "#d97706", marginBottom: 2 }}>Dispute {order.dispute.status.replace(/_/g, " ")}</div>
              <div style={{ fontSize: 13, color: "#b45309" }}>Tap to view dispute details and track progress →</div>
            </div>
          </div>
        )}

        {/* Completed */}
        {order.status === "COMPLETED" && (
          <div style={s.successBanner}>
            <span style={{ fontSize: 28 }}>🎉</span>
            <div>
              <div style={{ fontWeight: 700, color: "#16a34a", marginBottom: 2 }}>Order Completed</div>
              <div style={{ fontSize: 13, color: "#16a34a", opacity: 0.8 }}>This order has been successfully completed.</div>
            </div>
          </div>
        )}

        {/* Canceled */}
        {isCanceled && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, background: "rgba(107,114,128,0.08)", border: "1px solid rgba(107,114,128,0.2)", borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <span style={{ fontSize: 24 }}>🚫</span>
            <div>
              <div style={{ fontWeight: 700, color: "#6b7280", marginBottom: 2 }}>Order {order.status === "REFUNDED" ? "Refunded" : "Canceled"}</div>
              <div style={{ fontSize: 13, color: "#6b7280", opacity: 0.8 }}>This order is no longer active.</div>
            </div>
          </div>
        )}

        {/* Seller waiting */}
        {isSeller && order.sellerConfirmed && !order.buyerConfirmed && !hasActiveDispute && (
          <div style={s.infoBanner}>
            <span style={{ fontSize: 24 }}>⏳</span>
            <div>
              <div style={{ fontWeight: 700, color: "#3b82f6", marginBottom: 2 }}>Waiting for Buyer</div>
              <div style={{ fontSize: 13, color: "#3b82f6", opacity: 0.8 }}>The buyer needs to confirm receipt before payment is released.</div>
            </div>
          </div>
        )}

        {/* Buyer waiting for seller */}
        {isBuyer && !order.sellerConfirmed && order.payment && !isCanceled && (
          <div style={s.infoBanner}>
            <span style={{ fontSize: 24 }}>⏳</span>
            <div>
              <div style={{ fontWeight: 700, color: "#3b82f6", marginBottom: 2 }}>Waiting for Seller</div>
              <div style={{ fontSize: 13, color: "#3b82f6", opacity: 0.8 }}>The seller needs to confirm shipment before you can confirm receipt.</div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div>
          {canPay && (
            <button style={s.primaryBtn} onClick={() => navigate(`/checkout?orderId=${order.id}`)}>
              💳 Complete Payment
            </button>
          )}
          {sellerCanConfirm && (
            <button style={s.purpleBtn} onClick={() => navigate(`/delivery/${order.id}`)}>
              📦 Confirm Shipped
            </button>
          )}
          {buyerCanConfirm && (
            <button style={s.successBtn} onClick={() => setShowConfirm(true)}>
              Did you receive this order?
            </button>
          )}
          {canDispute && !hasActiveDispute && (
            <button style={s.warningBtn} onClick={() => navigate(`/dispute/${order.id}`)}>
              ⚠️ File a Dispute
            </button>
          )}
          {canCancel && (
            <button style={s.dangerOutlineBtn} onClick={async () => {
              if (!window.confirm("Cancel this order?")) return;
              const res = await fetch(`${API_BASE_URL}/orders/${order.id}/cancel`, { method: "PUT", headers: authHeaders() });
              if (res.ok) { await loadOrder(); }
              else { const d = await res.json(); window.alert(d.error || "Could not cancel"); }
            }}>
              Cancel Order
            </button>
          )}
        </div>
      </div>

      {/* Confirm Receipt Modal */}
      {showConfirm && (
        <div style={s.overlay} onClick={() => setShowConfirm(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: "#0f172a" }}>Did you receive this order?</div>
            <div style={{ fontSize: 14, color: "#64748b", marginBottom: 24 }}>
              Confirming releases payment to the seller. If you have an issue with the item, choose No to file a dispute instead.
            </div>
            <button
              style={{ ...s.successBtn, marginBottom: 10, opacity: confirming ? 0.5 : 1 }}
              onClick={() => handleBuyerConfirm(true)}
              disabled={confirming}
            >
              {confirming ? "Confirming..." : "Yes — I received it"}
            </button>
            <button
              style={s.dangerOutlineBtn}
              onClick={() => handleBuyerConfirm(false)}
            >
              No — Something is wrong
            </button>
          </div>
        </div>
      )}
    </div>
  );
}